import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { purchaseOrderAPI, grnAPI, userAPI, itemAPI, categoryAPI, unitAPI, clientAPI } from '../lib/api';
import { compressImageFile } from '../lib/imageCompress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ClipboardCheck, 
  ArrowLeft,
  Loader2,
  Package,
  CheckCircle2,
  AlertTriangle,
  Truck,
  Save,
  Upload,
  FileText,
  X,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';
import axios from 'axios';

// Use relative URLs for production compatibility - always use empty string for deployed environments
const BACKEND_URL = '';

export const GRNCreatePage = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [poStatus, setPOStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [receivedBy, setReceivedBy] = useState('');
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [billNumber, setBillNumber] = useState('');
  const [bills, setBills] = useState([]); // [{ url, name }]
  const billFileRef = useRef(null);
  
  // Additional items (not in PO) state
  const [additionalItems, setAdditionalItems] = useState([]);
  // Multi-client allocation state
  const [poClients, setPoClients] = useState([]); // [{id, name}]
  const [allocations, setAllocations] = useState({}); // { client_id: amountString }
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [creatingItem, setCreatingItem] = useState(false);
  const [newItemForm, setNewItemForm] = useState({
    name: '',
    category_id: '',
    unit_id: '',
    brand: '',
    quantity: '',
    rate: ''
  });

  useEffect(() => {
    fetchData();
  }, [poId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, usersRes, categoriesRes, unitsRes, poRes] = await Promise.all([
        purchaseOrderAPI.getReceivingStatus(poId),
        userAPI.getAll(),
        categoryAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        purchaseOrderAPI.getById(poId)
      ]);
      setPOStatus(statusRes.data);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data);
      setUnits(unitsRes.data);

      // Resolve clients attached to this PO (for multi-client amount allocation)
      const po = poRes.data;
      const cids = (po.client_ids && po.client_ids.length > 0) ? po.client_ids : (po.client_id ? [po.client_id] : []);
      const cnames = po.client_names || [];
      setPoClients(cids.map((id, i) => ({ id, name: cnames[i] || 'Client' })));
      
      // Initialize items with 0 received quantity and editable rate
      setItems(statusRes.data.items.map(item => ({
        ...item,
        received_quantity: 0,
        received_rate: item.rate || 0  // Allow editing the rate
      })));
    } catch (error) {
      toast.error('Failed to load PO details');
      navigate('/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (itemId, value) => {
    const qty = parseFloat(value) || 0;
    setItems(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, received_quantity: qty }
        : item
    ));
  };

  const handleRateChange = (itemId, value) => {
    const rate = parseFloat(value) || 0;
    setItems(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, received_rate: rate }
        : item
    ));
  };

  const handleBillUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp',
                          'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          'text/csv', 'application/csv', 'application/octet-stream'];

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploaded = [];
      for (const file of files) {
        if (!allowedTypes.includes(file.type) && file.type) {
          toast.error(`${file.name}: unsupported type`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: file size should be less than 10MB`);
          continue;
        }
        const toUpload = await compressImageFile(file);
        const formData = new FormData();
        formData.append('file', toUpload);
        const response = await axios.post(`${BACKEND_URL}/api/upload/document`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
        });
        uploaded.push({ url: `${BACKEND_URL}${response.data.url}`, name: file.name });
      }
      if (uploaded.length) {
        setBills(prev => [...prev, ...uploaded]);
        toast.success(`${uploaded.length} bill(s) uploaded`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload bill');
    } finally {
      setUploading(false);
      if (billFileRef.current) billFileRef.current.value = '';
    }
  };

  const removeBill = (index) => {
    setBills(prev => prev.filter((_, i) => i !== index));
  };

  const handleReceiveAll = (itemId) => {
    setItems(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, received_quantity: item.pending_quantity }
        : item
    ));
  };

  const handleReceiveAllItems = () => {
    setItems(prev => prev.map(item => ({
      ...item,
      received_quantity: item.pending_quantity
    })));
  };

  // Functions for adding new items not in PO
  const handleOpenAddItemDialog = () => {
    setNewItemForm({
      name: '',
      category_id: '',
      unit_id: '',
      brand: '',
      quantity: '',
      rate: ''
    });
    setAddItemDialogOpen(true);
  };

  const handleCreateAndAddItem = async () => {
    if (!newItemForm.name || !newItemForm.unit_id || !newItemForm.quantity || !newItemForm.rate) {
      toast.error('Please fill in item name, unit, quantity and rate');
      return;
    }

    try {
      setCreatingItem(true);
      
      // Create the item in the database
      const itemData = {
        name: newItemForm.name,
        category_id: newItemForm.category_id || null,
        default_unit_id: newItemForm.unit_id,
        brand: newItemForm.brand || null,
        status: 'active'
      };
      
      const response = await itemAPI.create(itemData);
      const createdItem = response.data;
      
      // Get unit name
      const unit = units.find(u => u.id === newItemForm.unit_id);
      
      // Add to additional items list
      const newAdditionalItem = {
        item_id: createdItem.id,
        item_name: createdItem.name,
        unit_id: newItemForm.unit_id,
        unit_name: unit?.name || '',
        brand: newItemForm.brand,
        quantity: parseFloat(newItemForm.quantity),
        rate: parseFloat(newItemForm.rate),
        is_new: true // Flag to indicate this is a new item not in PO
      };
      
      setAdditionalItems(prev => [...prev, newAdditionalItem]);
      setAddItemDialogOpen(false);
      toast.success(`Item "${createdItem.name}" created and added to GRN`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    } finally {
      setCreatingItem(false);
    }
  };

  const handleRemoveAdditionalItem = (index) => {
    setAdditionalItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleAdditionalItemChange = (index, field, value) => {
    setAdditionalItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: parseFloat(value) || 0 } : item
    ));
  };

  const getVarianceStatus = (item) => {
    const pending = item.pending_quantity;
    const received = item.received_quantity;
    
    if (received === 0 && pending > 0) return 'not_delivered';
    if (received === pending) return 'matched';
    if (received < pending) return 'short';
    return 'excess';
  };

  const getVarianceColor = (status) => {
    switch (status) {
      case 'matched': return 'bg-green-100 border-green-300 text-green-800';
      case 'short': return 'bg-red-100 border-red-300 text-red-800';
      case 'excess': return 'bg-amber-100 border-amber-300 text-amber-800';
      case 'not_delivered': return 'bg-gray-100 border-gray-300 text-gray-600';
      default: return '';
    }
  };

  const handleSubmit = async () => {
    if (!receivedBy) {
      toast.error('Please select who received the goods');
      return;
    }

    const itemsToSubmit = items.filter(item => item.received_quantity > 0 || item.pending_quantity > 0);
    const hasPoItems = itemsToSubmit.length > 0;
    const hasAdditionalItems = additionalItems.length > 0;
    
    if (!hasPoItems && !hasAdditionalItems) {
      toast.error('Please enter received quantities for at least one item or add additional items');
      return;
    }

    // Multi-client allocation validation
    const isMultiClient = poClients.length > 1;
    let clientAllocations = null;
    if (isMultiClient) {
      const grnTotal = items.reduce((sum, item) => sum + (item.received_quantity * (item.received_rate || item.rate)), 0)
        + additionalItems.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
      const allocSum = poClients.reduce((sum, c) => sum + (parseFloat(allocations[c.id]) || 0), 0);
      if (Math.abs(allocSum - grnTotal) > 1) {
        toast.error(`Client allocations (₹${allocSum.toLocaleString('en-IN')}) must equal the GRN total (₹${grnTotal.toLocaleString('en-IN')})`);
        return;
      }
      clientAllocations = poClients.map(c => ({
        client_id: c.id,
        client_name: c.name,
        amount: parseFloat(allocations[c.id]) || 0
      }));
    }

    try {
      setSaving(true);
      
      // Combine PO items and additional items
      const allItems = [
        ...itemsToSubmit.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          unit_id: item.unit_id,
          unit_name: item.unit_name,
          ordered_quantity: item.ordered_quantity,
          previously_received: item.received_quantity,
          received_quantity: item.received_quantity,
          rate: item.received_rate || item.rate,
          is_additional: false
        })),
        ...additionalItems.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          unit_id: item.unit_id,
          unit_name: item.unit_name,
          ordered_quantity: 0, // Not in original PO
          previously_received: 0,
          received_quantity: item.quantity,
          rate: item.rate,
          is_additional: true // Flag for additional items
        }))
      ];
      
      const grnData = {
        po_id: poId,
        received_by: receivedBy,
        receipt_date: receiptDate,
        notes: notes,
        bill_number: billNumber || null,
        bill_urls: bills.map(b => b.url),
        bill_url: bills[0]?.url || null,
        items: allItems,
        client_allocations: clientAllocations
      };

      const response = await grnAPI.create(grnData);
      toast.success(`GRN ${response.data.grn_number} created successfully!`);
      
      if (response.data.has_variance) {
        toast.warning('Note: Some items have quantity variance');
      }
      
      if (additionalItems.length > 0) {
        toast.info(`${additionalItems.length} additional item(s) added to inventory`);
      }
      
      navigate('/grn');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create GRN');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!poStatus) {
    return null;
  }

  const totalReceiving = items.reduce((sum, item) => sum + (item.received_quantity * (item.received_rate || item.rate)), 0);
  const totalAdditional = additionalItems.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  const grandTotal = totalReceiving + totalAdditional;
  const hasAnyInput = items.some(item => item.received_quantity > 0) || additionalItems.length > 0;

  return (
    <div className="space-y-6" data-testid="grn-create-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/purchase-orders')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6" />
            Receive Items - {poStatus.po_number}
          </h1>
          <p className="text-muted-foreground">Create Goods Receipt Note (GRN)</p>
        </div>
      </div>

      {/* PO Summary Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Purchase Order Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">PO Number</span>
              <p className="font-medium">{poStatus.po_number}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Vendor</span>
              <p className="font-medium">{poStatus.vendor_name}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">PO Date</span>
              <p className="font-medium">{formatDate(poStatus.po_date)}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant={poStatus.is_fully_received ? 'success' : 'default'}>
                {poStatus.fully_received_items}/{poStatus.total_items} Items Received
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receipt Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Receipt Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Receipt Date *</Label>
              <Input
                type="date"
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                data-testid="grn-receipt-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Received By *</Label>
              <Select value={receivedBy} onValueChange={setReceivedBy}>
                <SelectTrigger data-testid="grn-received-by">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Bill/Invoice Number</Label>
              <Input
                placeholder="Enter bill number"
                value={billNumber}
                onChange={(e) => setBillNumber(e.target.value)}
                data-testid="grn-bill-number"
              />
            </div>
          </div>
          
          {/* Bill Upload Section */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Attach Bill(s)/Invoice(s)</Label>
              <input
                type="file"
                ref={billFileRef}
                onChange={handleBillUpload}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                multiple
                className="hidden"
              />
              <div 
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                onClick={() => billFileRef.current?.click()}
                data-testid="grn-bill-upload-area"
              >
                {uploading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Uploading...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm">{bills.length > 0 ? 'Add more bills' : 'Click to upload bills (PDF, Word, Excel, Image)'}</span>
                  </div>
                )}
              </div>
              {bills.length > 0 && (
                <div className="space-y-1">
                  {bills.map((b, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg" data-testid={`grn-bill-item-${index}`}>
                      <FileText className="h-4 w-4 text-green-600 shrink-0" />
                      <span className="flex-1 text-sm truncate">{b.name}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeBill(index)}>
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="Any remarks..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="grn-notes"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items to Receive */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg">Items to Receive</CardTitle>
              <CardDescription>Enter the quantity received for each item</CardDescription>
            </div>
            <Button variant="outline" onClick={handleReceiveAllItems} data-testid="receive-all-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Receive All Pending
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {items.map((item) => {
              const variance = getVarianceStatus(item);
              const varianceColor = getVarianceColor(variance);
              const itemTotal = item.received_quantity * (item.received_rate || item.rate);
              
              return (
                <div 
                  key={item.item_id}
                  className={`p-4 rounded-lg border-2 transition-colors ${varianceColor}`}
                  data-testid={`grn-item-${item.item_id}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-7 gap-4 items-center">
                    {/* Item Name */}
                    <div className="lg:col-span-2">
                      <p className="font-medium text-lg">{item.item_name}</p>
                      <p className="text-sm text-muted-foreground">{item.unit_name}</p>
                    </div>
                    
                    {/* Quantities */}
                    <div className="grid grid-cols-3 gap-2 text-center lg:col-span-1">
                      <div>
                        <p className="text-xs text-muted-foreground">Ordered</p>
                        <p className="font-medium">{item.ordered_quantity}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Prev.</p>
                        <p className="font-medium">{item.received_quantity}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Pending</p>
                        <p className="font-bold text-primary">{item.pending_quantity}</p>
                      </div>
                    </div>
                    
                    {/* Received Input */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Receiving Qty</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.received_quantity || ''}
                          onChange={(e) => handleQuantityChange(item.item_id, e.target.value)}
                          className="text-lg font-bold text-center h-12"
                          placeholder="0"
                          data-testid={`grn-qty-${item.item_id}`}
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReceiveAll(item.item_id)}
                        className="mt-5"
                        disabled={item.pending_quantity === 0}
                      >
                        All
                      </Button>
                    </div>
                    
                    {/* Rate Input */}
                    <div>
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.received_rate || ''}
                        onChange={(e) => handleRateChange(item.item_id, e.target.value)}
                        className="text-center h-12"
                        placeholder="0.00"
                        data-testid={`grn-rate-${item.item_id}`}
                      />
                    </div>
                    
                    {/* Amount */}
                    <div className="text-center">
                      <Label className="text-xs">Amount</Label>
                      <p className="text-lg font-bold text-primary h-12 flex items-center justify-center">
                        {formatCurrency(itemTotal)}
                      </p>
                    </div>
                    
                    {/* Variance Indicator */}
                    <div className="text-center">
                      {variance === 'matched' && item.received_quantity > 0 && (
                        <div className="flex items-center justify-center gap-1 text-green-600">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-medium">Matched</span>
                        </div>
                      )}
                      {variance === 'short' && (
                        <div className="flex items-center justify-center gap-1 text-red-600">
                          <AlertTriangle className="h-5 w-5" />
                          <span className="font-medium">Short by {item.pending_quantity - item.received_quantity}</span>
                        </div>
                      )}
                      {variance === 'excess' && (
                        <div className="flex items-center justify-center gap-1 text-amber-600">
                          <AlertTriangle className="h-5 w-5" />
                          <span className="font-medium">Excess by {item.received_quantity - item.pending_quantity}</span>
                        </div>
                      )}
                      {variance === 'not_delivered' && item.pending_quantity > 0 && (
                        <div className="text-gray-500 text-sm">
                          Not receiving
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Additional Items Section (Items not in PO) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Additional Items (Not in PO)
              </CardTitle>
              <CardDescription>
                Add items received that were not in the original Purchase Order. New items will be added to your inventory.
              </CardDescription>
            </div>
            <Button onClick={handleOpenAddItemDialog} data-testid="add-new-item-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add New Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {additionalItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No additional items added yet</p>
              <p className="text-sm">Click "Add New Item" to add items that are not in the original PO</p>
            </div>
          ) : (
            <div className="space-y-3">
              {additionalItems.map((item, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-lg border-2 bg-blue-50 border-blue-200"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 items-center">
                    <div className="lg:col-span-2">
                      <p className="font-medium">{item.item_name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{item.unit_name}</span>
                        {item.brand && <Badge variant="outline" className="text-xs">{item.brand}</Badge>}
                        <Badge className="bg-blue-100 text-blue-700 text-xs">New Item</Badge>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs">Quantity</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleAdditionalItemChange(index, 'quantity', e.target.value)}
                        className="text-center h-10"
                        data-testid={`additional-qty-${index}`}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => handleAdditionalItemChange(index, 'rate', e.target.value)}
                        className="text-center h-10"
                        data-testid={`additional-rate-${index}`}
                      />
                    </div>
                    
                    <div className="text-center">
                      <Label className="text-xs">Amount</Label>
                      <p className="text-lg font-bold text-blue-600">
                        {formatCurrency(item.quantity * item.rate)}
                      </p>
                    </div>
                    
                    <div className="flex justify-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveAdditionalItem(index)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-additional-${index}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="flex justify-end pt-2">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Additional Items Total</p>
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(totalAdditional)}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Multi-client Amount Allocation */}
      {poClients.length > 1 && (
        <Card className="border-amber-300" data-testid="grn-client-allocation">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4" />
              Allocate Amount per Client
            </CardTitle>
            <CardDescription>
              This PO is linked to {poClients.length} clients. Split the bill amount ({formatCurrency(grandTotal)}) across them.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const per = Math.round((grandTotal / poClients.length) * 100) / 100;
                  const next = {};
                  poClients.forEach((c, i) => {
                    next[c.id] = (i === poClients.length - 1
                      ? Math.round((grandTotal - per * (poClients.length - 1)) * 100) / 100
                      : per).toString();
                  });
                  setAllocations(next);
                }}
                data-testid="grn-split-equally-btn"
              >
                Split equally
              </Button>
            </div>
            {poClients.map(c => (
              <div key={c.id} className="flex items-center gap-3" data-testid={`grn-alloc-row-${c.id}`}>
                <Label className="flex-1 text-sm font-medium">{c.name}</Label>
                <div className="relative w-44">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="pl-7"
                    value={allocations[c.id] || ''}
                    onChange={(e) => setAllocations({ ...allocations, [c.id]: e.target.value })}
                    placeholder="0"
                    data-testid={`grn-alloc-input-${c.id}`}
                  />
                </div>
              </div>
            ))}
            {(() => {
              const allocSum = poClients.reduce((s, c) => s + (parseFloat(allocations[c.id]) || 0), 0);
              const remaining = grandTotal - allocSum;
              return (
                <div className="flex justify-between border-t pt-2 text-sm" data-testid="grn-alloc-summary">
                  <span className="text-muted-foreground">Allocated: <span className="font-semibold text-foreground">{formatCurrency(allocSum)}</span></span>
                  <span className={Math.abs(remaining) > 1 ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                    {Math.abs(remaining) > 1 ? `Remaining: ${formatCurrency(remaining)}` : 'Fully allocated'}
                  </span>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}


      {/* Submit Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm text-muted-foreground">Total Value of This Receipt (Bill Amount)</p>
              <p className="text-3xl font-bold text-primary">{formatCurrency(grandTotal)}</p>
              {additionalItems.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  (PO Items: {formatCurrency(totalReceiving)} + Additional: {formatCurrency(totalAdditional)})
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/purchase-orders')}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={saving || !hasAnyInput}
                className="min-w-[150px]"
                data-testid="submit-grn-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating GRN...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create GRN
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add New Item Dialog */}
      <Dialog open={addItemDialogOpen} onOpenChange={setAddItemDialogOpen}>
        <DialogContent className="w-[95vw] max-w-lg md:max-w-xl lg:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add New Item to GRN
            </DialogTitle>
            <DialogDescription>
              Create a new item and add it to this GRN. The item will be saved to your inventory database.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                placeholder="Enter item name"
                value={newItemForm.name}
                onChange={(e) => setNewItemForm({ ...newItemForm, name: e.target.value })}
                data-testid="new-item-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={newItemForm.category_id || "none"}
                  onValueChange={(value) => setNewItemForm({ ...newItemForm, category_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="new-item-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Unit *</Label>
                <Select
                  value={newItemForm.unit_id || "none"}
                  onValueChange={(value) => setNewItemForm({ ...newItemForm, unit_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="new-item-unit">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select Unit</SelectItem>
                    {units.map(unit => (
                      <SelectItem key={unit.id} value={unit.id}>{unit.name} ({unit.abbreviation})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Brand (Optional)</Label>
              <Input
                placeholder="Enter brand name"
                value={newItemForm.brand}
                onChange={(e) => setNewItemForm({ ...newItemForm, brand: e.target.value })}
                data-testid="new-item-brand"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity Received *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={newItemForm.quantity}
                  onChange={(e) => setNewItemForm({ ...newItemForm, quantity: e.target.value })}
                  data-testid="new-item-quantity"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Rate (₹) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={newItemForm.rate}
                  onChange={(e) => setNewItemForm({ ...newItemForm, rate: e.target.value })}
                  data-testid="new-item-rate"
                />
              </div>
            </div>
            
            {newItemForm.quantity && newItemForm.rate && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Amount</p>
                <p className="text-xl font-bold text-primary">
                  {formatCurrency(parseFloat(newItemForm.quantity || 0) * parseFloat(newItemForm.rate || 0))}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAndAddItem} 
              disabled={creatingItem}
              data-testid="create-and-add-item-btn"
            >
              {creatingItem ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create & Add Item
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GRNCreatePage;
